/**
 * Leg Navigator Component
 * Tab-based navigation for multi-leg flights with quick add functionality
 */

import React, { useState, useCallback, useEffect } from 'react';

const LegNavigator = ({
  legs = [],
  documentType = 'flight',
  onLegChange,
  onAddLeg,
  onRemoveLeg,
  disabled = false,
  className = ''
}) => {
  const [activeLegIndex, setActiveLegIndex] = useState(0);

  // Reset active leg when legs change
  useEffect(() => {
    if (activeLegIndex >= legs.length && legs.length > 0) {
      setActiveLegIndex(legs.length - 1);
    }
  }, [legs.length, activeLegIndex]);

  // Handle keyboard shortcuts for leg navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle if not in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Number keys 1-9 to switch legs
      if (e.key >= '1' && e.key <= '9' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const legIndex = parseInt(e.key) - 1;
        if (legIndex < legs.length) {
          setActiveLegIndex(legIndex);
          e.preventDefault();
        }
      }

      // + or = to add leg
      if ((e.key === '+' || e.key === '=') && !e.ctrlKey && !e.metaKey && !disabled) {
        onAddLeg?.();
        e.preventDefault();
      }

      // - to remove current leg (with confirmation via shift)
      if (e.key === '-' && e.shiftKey && !disabled && legs.length > 1) {
        onRemoveLeg?.(activeLegIndex);
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [legs.length, activeLegIndex, onAddLeg, onRemoveLeg, disabled]);

  // Handle field change for active leg
  const handleFieldChange = useCallback((field, value) => {
    if (legs[activeLegIndex]) {
      const updatedLeg = { ...legs[activeLegIndex], [field]: value };
      onLegChange?.(activeLegIndex, updatedLeg);
    }
  }, [activeLegIndex, legs, onLegChange]);

  // Get route display for tab label
  const getRouteLabel = (leg, index) => {
    const from = leg.departure_airport || leg.departure_airport_icao || '?';
    const to = leg.arrival_airport || leg.arrival_airport_icao || '?';
    if (from !== '?' || to !== '?') {
      return `${from} → ${to}`;
    }
    return `Leg ${index + 1}`;
  };

  const activeLeg = legs[activeLegIndex] || {};
  const isPrivateFlight = documentType === 'private_flight';

  return (
    <div className={`${className}`}>
      {/* Tab Header */}
      <div className="flex items-center gap-1 mb-3 overflow-x-auto pb-1">
        {legs.map((leg, index) => (
          <button
            key={index}
            onClick={() => setActiveLegIndex(index)}
            className={`
              px-3 py-1.5 text-xs font-medium rounded-t-lg border border-b-0 whitespace-nowrap
              transition-colors flex items-center gap-1.5
              ${index === activeLegIndex
                ? 'bg-white text-blue-600 border-gray-200 relative z-10'
                : 'bg-gray-50 text-gray-600 border-transparent hover:bg-gray-100'
              }
            `}
          >
            <span className="w-4 h-4 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-[10px] font-bold">
              {index + 1}
            </span>
            <span>{getRouteLabel(leg, index)}</span>
            {leg.flight_number && !isPrivateFlight && (
              <span className="text-gray-400 ml-1">
                {leg.airline_code || ''}{leg.flight_number}
              </span>
            )}
          </button>
        ))}

        {/* Add Leg Button */}
        {!disabled && (
          <button
            onClick={() => {
              onAddLeg?.();
              // Set active to new leg after a tick
              setTimeout(() => setActiveLegIndex(legs.length), 0);
            }}
            className="px-2 py-1.5 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded flex items-center gap-1 border border-transparent"
            title="Add leg (+)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Leg
          </button>
        )}
      </div>

      {/* Active Leg Content */}
      {legs.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden -mt-px">
          {/* Leg header with remove button */}
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
            <span className="text-xs font-medium text-gray-700">
              Leg {activeLegIndex + 1} of {legs.length}
              {legs.length > 1 && (
                <span className="text-gray-400 ml-2">
                  (Press 1-{Math.min(9, legs.length)} to switch)
                </span>
              )}
            </span>
            {!disabled && legs.length > 1 && (
              <button
                onClick={() => onRemoveLeg?.(activeLegIndex)}
                className="p-1 text-gray-400 hover:text-red-500 rounded"
                title="Remove this leg (Shift+-)"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>

          {/* Leg fields */}
          <div className="p-3 space-y-3">
            {/* Route row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {isPrivateFlight ? 'From (ICAO)' : 'From (IATA)'}
                </label>
                <input
                  type="text"
                  value={activeLeg.departure_airport || activeLeg.departure_airport_icao || ''}
                  onChange={(e) => {
                    const field = isPrivateFlight ? 'departure_airport_icao' : 'departure_airport';
                    handleFieldChange(field, e.target.value.toUpperCase());
                  }}
                  disabled={disabled}
                  placeholder={isPrivateFlight ? 'e.g., KJFK' : 'e.g., JFK'}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {isPrivateFlight ? 'To (ICAO)' : 'To (IATA)'}
                </label>
                <input
                  type="text"
                  value={activeLeg.arrival_airport || activeLeg.arrival_airport_icao || ''}
                  onChange={(e) => {
                    const field = isPrivateFlight ? 'arrival_airport_icao' : 'arrival_airport';
                    handleFieldChange(field, e.target.value.toUpperCase());
                  }}
                  disabled={disabled}
                  placeholder={isPrivateFlight ? 'e.g., KLAX' : 'e.g., LAX'}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                />
              </div>
            </div>

            {/* Date/Time row */}
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Dep. Date</label>
                <input
                  type="date"
                  value={activeLeg.departure_date?.slice(0, 10) || ''}
                  onChange={(e) => handleFieldChange('departure_date', e.target.value)}
                  disabled={disabled}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Dep. Time</label>
                <input
                  type="time"
                  value={activeLeg.departure_time?.slice(0, 5) || ''}
                  onChange={(e) => handleFieldChange('departure_time', e.target.value)}
                  disabled={disabled}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Arr. Date</label>
                <input
                  type="date"
                  value={activeLeg.arrival_date?.slice(0, 10) || ''}
                  onChange={(e) => handleFieldChange('arrival_date', e.target.value)}
                  disabled={disabled}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Arr. Time</label>
                <input
                  type="time"
                  value={activeLeg.arrival_time?.slice(0, 5) || ''}
                  onChange={(e) => handleFieldChange('arrival_time', e.target.value)}
                  disabled={disabled}
                  className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Commercial flight details */}
            {!isPrivateFlight && (
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Airline</label>
                  <input
                    type="text"
                    value={activeLeg.airline || ''}
                    onChange={(e) => handleFieldChange('airline', e.target.value)}
                    disabled={disabled}
                    placeholder="e.g., United"
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Code</label>
                  <input
                    type="text"
                    value={activeLeg.airline_code || ''}
                    onChange={(e) => handleFieldChange('airline_code', e.target.value.toUpperCase())}
                    disabled={disabled}
                    placeholder="e.g., UA"
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Flight #</label>
                  <input
                    type="text"
                    value={activeLeg.flight_number || ''}
                    onChange={(e) => handleFieldChange('flight_number', e.target.value)}
                    disabled={disabled}
                    placeholder="e.g., 123"
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Class</label>
                  <input
                    type="text"
                    value={activeLeg.class_type || activeLeg.cabin_class || ''}
                    onChange={(e) => handleFieldChange('class_type', e.target.value)}
                    disabled={disabled}
                    placeholder="e.g., Economy"
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {/* Additional commercial flight fields */}
            {!isPrivateFlight && (
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Seat</label>
                  <input
                    type="text"
                    value={activeLeg.seat || ''}
                    onChange={(e) => handleFieldChange('seat', e.target.value.toUpperCase())}
                    disabled={disabled}
                    placeholder="e.g., 12A"
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Terminal</label>
                  <input
                    type="text"
                    value={activeLeg.departure_terminal || ''}
                    onChange={(e) => handleFieldChange('departure_terminal', e.target.value)}
                    disabled={disabled}
                    placeholder="e.g., T4"
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Gate</label>
                  <input
                    type="text"
                    value={activeLeg.departure_gate || ''}
                    onChange={(e) => handleFieldChange('departure_gate', e.target.value.toUpperCase())}
                    disabled={disabled}
                    placeholder="e.g., B22"
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Aircraft</label>
                  <input
                    type="text"
                    value={activeLeg.aircraft_type || ''}
                    onChange={(e) => handleFieldChange('aircraft_type', e.target.value)}
                    disabled={disabled}
                    placeholder="e.g., 777-200"
                    className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {/* Private flight specific fields */}
            {isPrivateFlight && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Departure FBO</label>
                    <input
                      type="text"
                      value={activeLeg.departure_fbo_name || ''}
                      onChange={(e) => handleFieldChange('departure_fbo_name', e.target.value)}
                      disabled={disabled}
                      placeholder="e.g., Signature Flight Support"
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Arrival FBO</label>
                    <input
                      type="text"
                      value={activeLeg.arrival_fbo_name || ''}
                      onChange={(e) => handleFieldChange('arrival_fbo_name', e.target.value)}
                      disabled={disabled}
                      placeholder="e.g., Atlantic Aviation"
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Tail Number</label>
                    <input
                      type="text"
                      value={activeLeg.tail_number || ''}
                      onChange={(e) => handleFieldChange('tail_number', e.target.value.toUpperCase())}
                      disabled={disabled}
                      placeholder="e.g., N123AB"
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Aircraft Type</label>
                    <input
                      type="text"
                      value={activeLeg.aircraft_type || ''}
                      onChange={(e) => handleFieldChange('aircraft_type', e.target.value)}
                      disabled={disabled}
                      placeholder="e.g., Gulfstream G650"
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Passengers</label>
                    <input
                      type="number"
                      value={activeLeg.pax_count || ''}
                      onChange={(e) => handleFieldChange('pax_count', parseInt(e.target.value) || '')}
                      disabled={disabled}
                      placeholder="e.g., 4"
                      className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        /* Empty state */
        <div className="text-center py-6 text-sm text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <svg className="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
          No legs defined.
          {!disabled && (
            <button
              onClick={onAddLeg}
              className="block mx-auto mt-2 px-3 py-1 text-blue-600 hover:text-blue-800 border border-blue-200 rounded hover:bg-blue-50"
            >
              Add First Leg
            </button>
          )}
        </div>
      )}

      {/* Keyboard hints */}
      {legs.length > 1 && (
        <div className="mt-2 text-xs text-gray-400 text-center">
          Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600">1</kbd>-
          <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600">{Math.min(9, legs.length)}</kbd> to switch legs
          {' '} | {' '}
          <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600">+</kbd> add leg
        </div>
      )}
    </div>
  );
};

export default LegNavigator;
